import { Box, Paper, Typography, useTheme } from '@mui/material'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { getEventEx } from '../../../store/EventById/selectors'
import { getActiveSubControl } from '../../../store/SubControlById/selectors'
import {
	Control,
	EventEx,
	initEventCo2,
	Scenario,
	timePatternsBase,
	TimePattern,
} from '../../../types'

type Props = {
	scenario: Scenario
	control: Control
}

const timePatternLabels: Record<TimePattern, string> = {
	base_t1: '夜間',
	base_t2: '午前',
	base_t3: '午後',
	qd: 'QD',
}

// 24h上での各ゾーンの位置（%）
// t1: 0:00-6:00 + 18:00-24:00, t2: 6:00-12:00, t3: 12:00-18:00
const zones = [
	{
		tp: 'base_t1' as const,
		label: '夜(〜日の出)',
		start: 0,
		end: 25,
		colorIdx: 0,
	},
	{
		tp: 'base_t2' as const,
		label: '午前(日の出〜正午)',
		start: 25,
		end: 50,
		colorIdx: 1,
	},
	{
		tp: 'base_t3' as const,
		label: '午後(正午〜日の入)',
		start: 50,
		end: 75,
		colorIdx: 2,
	},
	{
		tp: 'base_t1' as const,
		label: '夜(日の入〜)',
		start: 75,
		end: 100,
		colorIdx: 0,
	},
]

const Co2TimelineOverview = ({ scenario, control }: Props) => {
	const theme = useTheme()
	const activeSubc = useSelector((s) => getActiveSubControl(s, control))

	// 夜間 / 午前 / 午後。テーマ色に合わせる (夜間は中立な blueGrey)
	const enabledColors = [
		'#546e7a',
		theme.palette.primary.main,
		theme.palette.secondary.main,
	]
	const disabledColor = '#cfd8dc'

	const eventsByTp: Record<string, EventEx> = {}
	for (const tp of timePatternsBase) {
		eventsByTp[tp] = useSelector((s) =>
			getEventEx(s, scenario.eventIds, tp, activeSubc, initEventCo2),
		)
	}

	return (
		<Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 3 }}>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				CO2 タイムライン概要
			</Typography>

			{/* タイムラインバー */}
			<Box
				sx={{
					display: 'flex',
					height: 36,
					borderRadius: 1,
					overflow: 'hidden',
					mb: 1,
				}}
			>
				{zones.map((zone, i) => {
					const ev = eventsByTp[zone.tp]
					const isEnabled = ev.enabled
					const width = `${zone.end - zone.start}%`
					const bg = isEnabled ? enabledColors[zone.colorIdx] : disabledColor

					return (
						<Box
							key={i}
							sx={{
								width,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: bg,
								color: 'white',
								fontSize: 12,
								fontWeight: 'bold',
								borderRight:
									i < zones.length - 1
										? '1px solid rgba(255,255,255,0.3)'
										: 'none',
								opacity: isEnabled ? 1 : 0.5,
							}}
						>
							{isEnabled ? `${ev.co2Above}ppm` : 'OFF'}
						</Box>
					)
				})}
			</Box>

			{/* 時刻ラベル */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					fontSize: 11,
					color: '#666',
					mb: 2,
				}}
			>
				<span>0:00</span>
				<span>日の出</span>
				<span>正午</span>
				<span>日の入り</span>
				<span>24:00</span>
			</Box>

			{/* 各パターンの詳細 */}
			<Box sx={{ display: 'flex', gap: 1 }}>
				{timePatternsBase.map((tp, i) => {
					const ev = eventsByTp[tp]
					const isEnabled = ev.enabled
					const bg = isEnabled ? enabledColors[i] : disabledColor

					return (
						<Paper
							key={tp}
							variant="outlined"
							sx={{
								flex: 1,
								p: 1.5,
								opacity: isEnabled ? 1 : 0.5,
								borderLeft: `4px solid ${bg}`,
							}}
						>
							<Typography variant="caption" sx={{ fontWeight: 'bold' }}>
								{timePatternLabels[tp]}
							</Typography>
							{isEnabled ? (
								<Box sx={{ fontSize: 12, mt: 0.5 }}>
									<div>CO2: {ev.co2Above}ppm 以下でON</div>
									<div>窓チェック: {ev.windowCheck ? 'あり' : 'なし'}</div>
								</Box>
							) : (
								<Typography variant="body2" color="text.secondary">
									無効
								</Typography>
							)}
						</Paper>
					)
				})}
			</Box>
		</Paper>
	)
}

export default Co2TimelineOverview
